import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { api, apiError } from '../api/client';
import { useAuth } from './AuthContext';

const AttentionContext = createContext(null);
export const invalidateAttention = () => window.dispatchEvent(new Event('attention:invalidate'));

export function AttentionProvider({ children }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [offset, setOffset] = useState(0);
  const generation = useRef(0);
  const pending = useRef(null);
  const refresh = useCallback(async (force = true) => {
    if (!user || document.hidden || (!force && pending.current)) return;
    pending.current?.abort();
    const controller = new AbortController();
    pending.current = controller;
    const current = ++generation.current;
    try {
      const result = await api.getAttention({ limit: 50, offset }, { signal: controller.signal });
      if (current !== generation.current || controller.signal.aborted) return;
      setData(result);
      setError('');
      if (offset && result.total <= offset) setOffset(0);
    } catch (failure) {
      if (current === generation.current && !controller.signal.aborted) setError(apiError(failure));
    } finally {
      if (current === generation.current) pending.current = null;
    }
  }, [user, offset]);
  useEffect(() => {
    setData(null);
    setError('');
    setOffset(0);
  }, [user?.id]);
  useEffect(() => {
    refresh();
    const update = () => refresh();
    const timer = setInterval(() => refresh(false), 15000);
    window.addEventListener('focus', update);
    window.addEventListener('attention:invalidate', update);
    document.addEventListener('visibilitychange', update);
    return () => {
      ++generation.current;
      pending.current?.abort();
      pending.current = null;
      clearInterval(timer);
      window.removeEventListener('focus', update);
      window.removeEventListener('attention:invalidate', update);
      document.removeEventListener('visibilitychange', update);
    };
  }, [refresh]);
  return <AttentionContext.Provider value={{ data, error, offset, setOffset, refresh }}>{children}</AttentionContext.Provider>;
}

export function useAttention() {
  return useContext(AttentionContext);
}
